import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import type { OfferedCreditPack } from 'fym-shared/src/music24/creditPacks';
import { directedFootageSec } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { openSMLUpgradePrompt } from '../../../redux/slices/appInfo/slice';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { planNameMessages } from '../../music24/config/music24PlanNames';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { creditMessages } from '../../music24/promote/credits/creditMessages';
import { MemberOwnerNote } from '../../music24/promote/credits/MemberOwnerNote';
import { useBuyCreditPack } from '../../music24/promote/credits/useBuyCreditPack';
import { useVideoCredits } from '../../music24/promote/credits/useVideoCredits';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { useIsPhoneLayout } from '../../musicVideoEditor/phone/useIsPhoneLayout';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { useConfirmDirectedVideoMutation, useGetDirectedVideoPriceQuery } from '../directedVideoApi';
import { priceMessages as messages } from '../messages/priceMessages';
import { scriptMessages } from '../messages/scriptMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { AlertIcon } from './director/AlertIcon';
import { packPriceText, PricePacks } from './make/PricePacks';
import { useDisplayCurrency } from '../../music24/config/useDisplayCurrency';
import { confirmRefusalOf, missingCredits, packForMissing, priceOffer, type ConfirmRefusal } from './make/priceRules';
import { boldPart } from '../../common/richTextChildren';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the price and the make; each part uses its own slice of it */
import styles from './make/Make.module.scss';
import steps from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

// A key from the text of the part: a message with several bold parts has no duplicate key (QA skpr2-3).
const bold = boldPart;

/**
 * Step 5, Price (design directed-video f1 to f3, j4): "N new clips · S s of new footage = M credits"
 * (each clip costs by its length, `aiClipCreditsFor`), what is included, the balance and what is
 * left after it, the refund rules, and "Make the video". With too few credits the owner sees the credit packs and the
 * button is off; a member sees the owner note. Packs are for paid plans only (`M24_PACKS_NEED_PLAN`):
 * Free and the trial see the plan offer, and a plan with no AI footage (`canMakeAiFootage`) cannot
 * make the video at all (QA 2026-10-28, skpr2-1). "Make the video" sends `POST …/confirm` with the
 * script revision of the price; the button stays off until the answer, so a double click sends one.
 */
const PriceStepComponent: React.FC = () => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const { accountId, catalogItemUuid, video, readOnly, openStep } = useDirectedVideoContext();
  const isPhone = useIsPhoneLayout();
  const isNativeEmbed = useIsNativeEmbed();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: video.uuid }),
    [accountId, catalogItemUuid, video.uuid]
  );
  const priceRead = useGetDirectedVideoPriceQuery(args);
  const { credits } = useVideoCredits({ accountId });
  const buyer = useBuyCreditPack();
  const capabilities = useMusic24Capabilities();
  const dispatch = useDispatch();
  const [confirm, confirming] = useConfirmDirectedVideoMutation();
  const [refusal, setRefusal] = useState<ConfirmRefusal | null>(null);
  const [chosenPack, setChosenPack] = useState<OfferedCreditPack | null>(null);

  const price = priceRead.data;
  if (price === undefined) {
    return (
      <DirectedStepLayout>
        {priceRead.error === undefined ? (
          <LoadingView label={intl.formatMessage(messages.loading)} />
        ) : (
          <ErrorView
            error={priceRead.error}
            retry={() => {
              priceRead.refetch().catch(console.error);
            }}
          />
        )}
      </DirectedStepLayout>
    );
  }

  const total = price.priceCredits;
  // A server from before 2026-10-23 sends no seconds: they come from the script.
  const footageSec = price.footageSec ?? (video.script === null ? 0 : directedFootageSec(video.script));
  const missing = missingCredits(price.balance, total);
  const isShort = missing > 0;
  const isOwner = credits?.isOwner ?? true;
  const offer = priceOffer({
    canMakeAiFootage: capabilities.canMakeAiFootage,
    plan: credits?.plan ?? null,
    isShort,
    isOwner,
    isNativeEmbed,
  });
  const needsAiPlan = offer === 'aiPlan';
  const openPlans = () => dispatch(openSMLUpgradePrompt({ feature: SMLUpgradeFeature.aiFootage }));
  const pack = chosenPack ?? packForMissing(missing);
  const generatedPictures = video.cast.filter((entry) => entry.generated).length;
  const effects = video.script?.effects.length ?? 0;

  const make = async () => {
    setRefusal(null);
    try {
      await confirm({ ...args, scriptRevision: price.scriptRevision }).unwrap();
    } catch (caught) {
      console.error('Directed video: the make did not start', caught);
      const kind = confirmRefusalOf(caught);
      setRefusal(kind);
      if (kind === 'stale') {
        priceRead.refetch().catch(console.error);
      }
    }
  };

  let youHave = intl.formatMessage(messages.youHave);
  if (!isPhone && credits !== null && credits.limit !== null && credits.planLeft !== null) {
    const planName = intl.formatMessage(planNameMessages[credits.plan]);
    youHave =
      credits.packBalance > 0
        ? intl.formatMessage(messages.youHaveSplit, { plan: credits.planLeft, planName, packs: credits.packBalance })
        : intl.formatMessage(messages.youHavePlan, { plan: credits.planLeft, planName });
  }

  let refusalText: React.ReactNode = null;
  if (refusal === 'stale') {
    refusalText = (
      <>
        {intl.formatMessage(messages.stale, { b: bold, total, count: price.clipCount })}
        <button type="button" className={steps.alertAction} onClick={() => setRefusal(null)}>
          {intl.formatMessage(messages.seeNewPrice)}
        </button>
      </>
    );
  } else if (refusal !== null) {
    const texts = {
      otherMaking: messages.otherMaking,
      songRendering: messages.songRendering,
      budget: messages.budget,
      noCredits: messages.noCredits,
      failed: messages.confirmFailed,
    } as const;
    refusalText = intl.formatMessage(texts[refusal], { b: bold });
  }

  const breakdown = isPhone ? (
    <>
      <span className={styles.label}>{intl.formatMessage(messages.includedPhone)}</span>
      <span className={clsx(styles.value, styles.included)}>{intl.formatMessage(messages.included)}</span>
    </>
  ) : (
    <>
      <span className={styles.label}>{intl.formatMessage(messages.director)}</span>
      <span className={clsx(styles.value, styles.included)}>{intl.formatMessage(messages.included)}</span>
      <span className={styles.label}>{intl.formatMessage(messages.castPictures, { count: generatedPictures })}</span>
      <span className={clsx(styles.value, styles.included)}>{intl.formatMessage(messages.included)}</span>
      <span className={styles.label}>{intl.formatMessage(messages.effects, { count: effects })}</span>
      <span className={clsx(styles.value, styles.included)}>{intl.formatMessage(messages.included)}</span>
      <span className={styles.label}>{intl.formatMessage(messages.render)}</span>
      <span className={clsx(styles.value, styles.included)}>{intl.formatMessage(messages.included)}</span>
    </>
  );

  const canBuyHere = offer === 'packs';
  return (
    <DirectedStepLayout>
      <div className={shell.head}>
        <div className={shell.grow}>
          <h2>{intl.formatMessage(messages.title)}</h2>
          <p className={shell.sub}>{intl.formatMessage(messages.intro, { count: price.clipCount })}</p>
        </div>
      </div>
      <div className={styles.pricePage} data-testid="directed-price">
        <section className={styles.panel}>
          <span className={shell.eyebrow}>{intl.formatMessage(messages.eyebrow)}</span>
          <div className={styles.big}>
            <b>{total}</b>
            <span>{intl.formatMessage(messages.credits)}</span>
          </div>
          <div className={styles.equation}>
            {intl.formatMessage(scriptMessages.priceLineSeconds, {
              count: price.clipCount,
              seconds: footageSec,
              total,
            })}
          </div>
          {!isShort && <div className={styles.breakdown}>{breakdown}</div>}
          {canBuyHere && <PricePacks missing={missing} selected={pack} onSelect={setChosenPack} />}
        </section>
        <section className={clsx(styles.panel, styles.panelSide)}>
          <span className={shell.eyebrow}>{intl.formatMessage(messages.balanceEyebrow)}</span>
          <div className={styles.breakdown}>
            <span className={styles.label}>{youHave}</span>
            <span className={styles.value}>
              {price.balance === null
                ? intl.formatMessage(messages.noLimit)
                : intl.formatMessage(messages.amount, { count: price.balance })}
            </span>
            {price.balance !== null && !isShort && (
              <>
                <span className={styles.label}>{intl.formatMessage(messages.after)}</span>
                <span className={styles.value}>
                  {intl.formatMessage(messages.amount, { count: price.balance - total })}
                </span>
              </>
            )}
            {isShort && (
              <>
                <span className={styles.label}>{intl.formatMessage(messages.missing)}</span>
                <span className={clsx(styles.value, styles.negative)}>
                  {intl.formatMessage(messages.amount, { count: missing })}
                </span>
              </>
            )}
          </div>
          {(needsAiPlan || offer === 'plan') && (
            <div className={clsx(steps.alert, steps.alertInfo)} data-testid="directed-price-needs-plan">
              <AlertIcon kind="info" />
              <div>
                {intl.formatMessage(needsAiPlan ? messages.aiNeedsPlan : messages.packsNeedPlan)}
                {isOwner && !isNativeEmbed && (
                  <button type="button" className={steps.alertAction} onClick={openPlans}>
                    {intl.formatMessage(messages.seePlans)}
                  </button>
                )}
              </div>
            </div>
          )}
          {isShort && !isOwner && <MemberOwnerNote />}
          {isShort && isOwner && isNativeEmbed && (
            <p className={shell.hint}>{intl.formatMessage(creditMessages.nativeNoPacks)}</p>
          )}
          {!isShort && !needsAiPlan && (
            <>
              <span className={styles.rule} aria-hidden />
              <span className={shell.eyebrow}>{intl.formatMessage(messages.nextEyebrow)}</span>
              {isPhone ? (
                <p className={shell.hint}>{intl.formatMessage(messages.refundRulePhone, { total })}</p>
              ) : (
                <div className={clsx(steps.alert, steps.alertInfo)}>
                  <AlertIcon kind="info" />
                  <div>{intl.formatMessage(messages.refundRule, { total })}</div>
                </div>
              )}
            </>
          )}
          {refusalText !== null && (
            <div className={clsx(steps.alert, refusal === 'budget' ? steps.alertError : steps.alertWarn)} role="alert">
              <AlertIcon kind="warn" />
              <div>{refusalText}</div>
            </div>
          )}
          <div className={styles.confirm}>
            <SMLButton
              variant="primary"
              className={styles.large}
              disabled={readOnly || isShort || needsAiPlan || confirming.isLoading || priceRead.isFetching}
              onClick={() => {
                make().catch(console.error);
              }}
            >
              {intl.formatMessage(messages.make, { total })}
            </SMLButton>
            {canBuyHere && buyer.canBuy && (
              <SMLButton
                disabled={buyer.loadingPackId !== null}
                onClick={() => {
                  buyer.buy(pack.id).catch(console.error);
                }}
              >
                {intl.formatMessage(messages.buyPack, {
                  count: pack.credits,
                  price: packPriceText(intl, pack, currency),
                })}
              </SMLButton>
            )}
            {canBuyHere && !buyer.canBuy && (
              <p className={shell.hint}>{withBrandWord(intl.formatMessage(creditMessages.buyInBrowser))}</p>
            )}
            {!needsAiPlan && (
              <p className={shell.hint}>
                {isShort
                  ? intl.formatMessage(canBuyHere ? messages.packsHint : messages.shortHint)
                  : intl.formatMessage(messages.makeHint)}
              </p>
            )}
            <SMLButton className={steps.ghost} onClick={() => openStep('script')}>
              {intl.formatMessage(messages.backToScript)}
            </SMLButton>
          </div>
        </section>
      </div>
    </DirectedStepLayout>
  );
};

export const PriceStep = TypedMemo(PriceStepComponent);
